import React, { useState } from 'react';
import { Quote } from '../types';
import { CheckCircle2, X, Heart, Bookmark, Plus, Trash2 } from 'lucide-react';

interface MultiQuoteFormProps {
  onSubmit: (quotes: Array<Omit<Quote, 'id'>>) => Promise<Array<{ success: boolean; error?: string }>>;
}

interface QuoteFormData {
  text: string;
  author: string;
  age: string;
  location: string;
  context: string;
  year: string;
  isFavorite: boolean;
  quotewall: boolean;
}

interface SubmissionConfirmation {
  timestamp: Date;
  referenceNumber: string;
  quoteCount: number;
}

const emptyQuoteForm = (): QuoteFormData => ({
  text: '',
  author: '',
  age: '',
  location: '',
  context: '',
  year: '',
  isFavorite: false,
  quotewall: false
});

const generateReferenceNumber = () => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 7);
  return `QT-${timestamp}-${random}`.toUpperCase();
};

export default function MultiQuoteForm({ onSubmit }: MultiQuoteFormProps) {
  const [quotes, setQuotes] = useState<QuoteFormData[]>([emptyQuoteForm()]);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<SubmissionConfirmation | null>(null);

  const validateQuote = (quote: QuoteFormData, index: number) => {
    const quoteErrors: Record<string, string> = {};

    if (!quote.text.trim()) {
      quoteErrors.text = 'Quote text is required';
    } else if (quote.text.length > 500) {
      quoteErrors.text = 'Quote must be less than 500 characters';
    }

    if (!quote.author.trim()) {
      quoteErrors.author = 'Author name is required';
    }

    if (quote.age && isNaN(Number(quote.age))) {
      quoteErrors.age = 'Age must be a number';
    }

    if (quote.year) {
      const yearNum = Number(quote.year);
      if (isNaN(yearNum) || yearNum < 1000 || yearNum > new Date().getFullYear()) {
        quoteErrors.year = 'Please enter a valid year';
      }
    }

    return quoteErrors;
  };

  const validateForm = () => {
    const newErrors: Record<number, Record<string, string>> = {};
    let hasErrors = false;

    quotes.forEach((quote, index) => {
      const quoteErrors = validateQuote(quote, index);
      if (Object.keys(quoteErrors).length > 0) {
        newErrors[index] = quoteErrors;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setConfirmation(null);
    
    if (validateForm()) {
      try {
        setSubmitting(true);

        const formattedQuotes = quotes.map(quote => ({
          text: quote.text,
          author: quote.author,
          age: Number(quote.age) || null,
          location: quote.location,
          context: quote.context,
          year: Number(quote.year) || null,
          isFavorite: quote.isFavorite,
          quotewall: quote.quotewall
        }));

        const results = await onSubmit(formattedQuotes);
        const allSuccess = results.every(result => result.success);

        if (allSuccess) {
          const confirmationData: SubmissionConfirmation = {
            timestamp: new Date(),
            referenceNumber: generateReferenceNumber(),
            quoteCount: quotes.length
          };
          setConfirmation(confirmationData);
          setQuotes([emptyQuoteForm()]);
        } else {
          const errors = results.filter(result => !result.success).map(result => result.error);
          setSubmitError(`Failed to submit some quotes: ${errors.join(', ')}`);
        }
      } catch (error) {
        console.error('Form submission error:', error);
        setSubmitError('An unexpected error occurred');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setQuotes(prev => prev.map((quote, i) => 
      i === index ? { ...quote, [name]: value } : quote
    ));
    
    if (errors[index]?.[name]) {
      setErrors(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          [name]: ''
        }
      }));
    }
  };

  const handleToggle = (index: number, field: 'isFavorite' | 'quotewall') => {
    setQuotes(prev => prev.map((quote, i) => 
      i === index ? { ...quote, [field]: !quote[field] } : quote
    ));
  };

  const addQuote = () => {
    setQuotes(prev => [...prev, emptyQuoteForm()]);
  };

  const removeQuote = (index: number) => {
    setQuotes(prev => prev.filter((_, i) => i !== index));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  return (
    <div className="space-y-8">
      {confirmation && (
        <div 
          role="alert"
          aria-live="polite"
          className="fixed top-4 left-1/2 transform -translate-x-1/2 w-full max-w-md z-50"
        >
          <div className="mx-4 p-4 bg-green-50 border border-green-100 rounded-lg shadow-lg">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p className="text-green-800 font-medium">
                Your quotes have been successfully submitted
              </p>
              <button 
                onClick={() => setConfirmation(null)}
                className="ml-auto text-green-700 hover:text-green-900 p-1"
                aria-label="Dismiss success message"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {submitError && (
        <div 
          role="alert"
          className="p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2"
        >
          <span className="text-sm sm:text-base">{submitError}</span>
          <button 
            onClick={() => setSubmitError(null)}
            className="ml-auto text-red-700 hover:text-red-900 p-2 -mr-1"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {quotes.map((quote, index) => (
          <div 
            key={index}
            className="bg-white rounded-lg shadow-md p-6 relative"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Quote #{index + 1} of {quotes.length}
              </h2>
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeQuote(index)}
                  className="text-red-600 hover:text-red-700 p-2 -mt-2 -mr-2"
                  aria-label="Remove quote"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor={`text-${index}`} className="block text-base font-medium text-gray-700 mb-2">
                  Quote Text <span className="text-red-500">*</span>
                </label>
                <textarea
                  id={`text-${index}`}
                  name="text"
                  value={quote.text}
                  onChange={(e) => handleChange(index, e)}
                  rows={3}
                  className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${
                    errors[index]?.text ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter the quote text..."
                />
                {errors[index]?.text && (
                  <p className="mt-2 text-sm text-red-600" role="alert">{errors[index].text}</p>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  {500 - quote.text.length} characters remaining
                </p>
              </div>

              <div>
                <label htmlFor={`author-${index}`} className="block text-base font-medium text-gray-700 mb-2">
                  Author <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`author-${index}`}
                  name="author"
                  value={quote.author}
                  onChange={(e) => handleChange(index, e)}
                  className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${
                    errors[index]?.author ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter the author's name"
                  autoComplete="off"
                />
                {errors[index]?.author && (
                  <p className="mt-2 text-sm text-red-600" role="alert">{errors[index].author}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor={`age-${index}`} className="block text-base font-medium text-gray-700 mb-2">
                    Age
                  </label>
                  <input
                    type="number"
                    id={`age-${index}`}
                    name="age"
                    value={quote.age}
                    onChange={(e) => handleChange(index, e)}
                    className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${
                      errors[index]?.age ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Author's age"
                    min="0"
                    max="150"
                  />
                  {errors[index]?.age && (
                    <p className="mt-2 text-sm text-red-600" role="alert">{errors[index].age}</p>
                  )}
                </div>

                <div>
                  <label htmlFor={`year-${index}`} className="block text-base font-medium text-gray-700 mb-2">
                    Year
                  </label>
                  <input
                    type="number"
                    id={`year-${index}`}
                    name="year"
                    value={quote.year}
                    onChange={(e) => handleChange(index, e)}
                    className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${
                      errors[index]?.year ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Year of quote"
                    min="1000"
                    max={new Date().getFullYear()}
                  />
                  {errors[index]?.year && (
                    <p className="mt-2 text-sm text-red-600" role="alert">{errors[index].year}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor={`location-${index}`} className="block text-base font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  id={`location-${index}`}
                  name="location"
                  value={quote.location}
                  onChange={(e) => handleChange(index, e)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="Where was the quote said?"
                  autoComplete="off"
                />
              </div>

              <div>
                <label htmlFor={`context-${index}`} className="block text-base font-medium text-gray-700 mb-2">
                  Context
                </label>
                <input
                  type="text"
                  id={`context-${index}`}
                  name="context"
                  value={quote.context}
                  onChange={(e) => handleChange(index, e)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  placeholder="What was the context of the quote?"
                  autoComplete="off"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  onClick={() => handleToggle(index, 'isFavorite')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                    quote.isFavorite
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : 'bg-gray-50 border-gray-200 text-gray-700'
                  } hover:bg-opacity-80 transition-colors`}
                >
                  <Heart className={`h-5 w-5 ${quote.isFavorite ? 'fill-red-500' : ''}`} />
                  {quote.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </button>

                <button
                  type="button"
                  onClick={() => handleToggle(index, 'quotewall')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                    quote.quotewall
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-gray-50 border-gray-200 text-gray-700'
                  } hover:bg-opacity-80 transition-colors`}
                >
                  <Bookmark className={`h-5 w-5 ${quote.quotewall ? 'fill-blue-500' : ''}`} />
                  {quote.quotewall ? 'Remove from Quotewall' : 'Add to Quotewall'}
                </button>
              </div>
            </div>
          </div>
        ))}

        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <button
            type="button"
            onClick={addQuote}
            className="flex items-center gap-2 px-6 py-2 text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Add Another Quote
          </button>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full sm:w-auto px-6 py-3 bg-blue-600 text-white text-lg font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${
              submitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {submitting ? 'Submitting Quotes...' : 'Submit All Quotes'}
          </button>
        </div>
      </form>
    </div>
  );
}